import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Loader2, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	getAsystentBusinessConfig,
	updateAsystentBusinessConfig,
} from "@/features/asystent/asystent-api";
import { useAuth } from "@/features/auth/use-auth";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { apiFetch } from "@/lib/auth-api";
import { ensureElementVisible } from "@/lib/route-guards";

export const Route = createFileRoute("/_auth/asystent-koszty")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.analityka.asystent-koszty",
			location.pathname,
		);
	},
	component: AsystentKosztyPage,
});

// ── Typy odpowiedzi backendu (camelCase) ──
interface WindowStat {
	label: string;
	calls: number;
	promptTokens: number;
	completionTokens: number;
	costUsd: number;
}
interface ModelStat {
	provider: string;
	model: string;
	calls: number;
	promptTokens: number;
	completionTokens: number;
	costUsd: number;
}
interface CallTypeStat {
	callType: string;
	calls: number;
	promptTokens: number;
	completionTokens: number;
	costUsd: number;
}
interface FeatureStat {
	feature: string;
	calls: number;
	promptTokens: number;
	completionTokens: number;
	costUsd: number;
}
interface DailyStat {
	day: string;
	calls: number;
	tokens: number;
	costUsd: number;
}
interface QueryStat {
	query: string;
	count: number;
}
interface LlmConfig {
	provider?: string;
	openaiModel?: string;
	dailyTokenCap?: number | null;
}
interface StatsResponse {
	windows: WindowStat[];
	byFeature: FeatureStat[];
	byModel: ModelStat[];
	byCallType: CallTypeStat[];
	daily: DailyStat[];
	topQueries: QueryStat[];
	totalCalls: number;
	avgLatencyMs: number;
	config: LlmConfig;
	priceNote: string;
}

const fmtUsd = (v: number) => `$${v < 1 ? v.toFixed(4) : v.toFixed(2)}`;
const PAGE_DESCRIPTION =
	"Realne zużycie tokenów AI: Asystent (korekta + strażnik), ulotki (opisy), kategoryzacja (batch). Koszt $ szacunkowy; tokeny dokładne z API.";
const fmtNum = (v: number) => v.toLocaleString("pl-PL");
const callTypeLabel = (t: string) =>
	t === "correction"
		? "Korekta literówek"
		: t === "guardian"
			? "Strażnik trafności"
			: t === "description"
				? "Opis z ulotki"
				: t.startsWith("batch_")
					? `Batch: ${t.slice(6)}`
					: t;
const featureLabel = (f: string) =>
	f === "asystent"
		? "Asystent"
		: f === "ulotki"
			? "Ulotki (opisy)"
			: f === "kategoryzacja"
				? "Kategoryzacja"
				: f;

interface ReformatProgress {
	total: number;
	done: number;
	remaining: number;
	running: boolean;
	repairTotal?: number;
	repairDone?: number;
	repairRemaining?: number;
	repairRunning?: boolean;
}

function ProgressRow({
	label,
	done,
	total,
	remaining,
}: {
	label: string;
	done: number;
	total: number;
	remaining: number;
}) {
	const pct = total > 0 ? Math.round((done / total) * 100) : 0;
	return (
		<div className="space-y-2">
			<div className="text-xs font-medium text-muted-foreground">{label}</div>
			<div className="flex items-baseline justify-between text-sm">
				<span className="font-medium tabular-nums text-foreground">
					{fmtNum(done)} / {fmtNum(total)} produktów
				</span>
				<span className="tabular-nums text-muted-foreground">zostało {fmtNum(remaining)}</span>
			</div>
			<div className="h-2 w-full overflow-hidden rounded-full bg-muted">
				<div
					className="h-full rounded-full bg-primary transition-all"
					style={{ width: `${pct}%` }}
				/>
			</div>
			<div className="text-xs tabular-nums text-muted-foreground">{pct}% ukończone</div>
		</div>
	);
}

function ReadOnlyConfigHint() {
	return (
		<p className="text-xs text-muted-foreground">Tylko super_admin może zmienić to ustawienie.</p>
	);
}

function AsystentKosztyPage() {
	const queryClient = useQueryClient();
	// Stronę widzi każdy z grantem sidebar.analityka.asystent-koszty, ale zapis obu
	// konfiguracji backend dopuszcza wyłącznie dla super_admin (twarda bramka, nie
	// widoczność) — pozostali dostają podgląd bez przycisków kończących się 403.
	const { hasRole } = useAuth();
	const canEditConfig = hasRole("super_admin");
	const isBelowMd = useIsBelowMd();
	const { data, isLoading, error } = useQuery({
		queryKey: ["asystent", "stats"],
		queryFn: () => apiFetch<StatsResponse>("/strateg-cenowy/asystent-stats"),
		staleTime: 60_000,
	});

	// Postep standaryzacji opisow (reformat ai_only -> sekcyjny). Liczby cache'owane
	// serwerowo 10 min (zapytanie ~13s) — polling co 60s trafia w cache.
	const reformat = useQuery({
		queryKey: ["enrichment", "reformat-progress"],
		queryFn: () => apiFetch<ReformatProgress>("/enrichment/reformat-progress"),
		staleTime: 5 * 60_000,
		refetchInterval: 60_000,
	});
	const businessConfig = useQuery({
		queryKey: ["asystent", "business-config"],
		queryFn: getAsystentBusinessConfig,
		staleTime: 60_000,
	});

	// Form przełącznika — inicjowany z configu, edytowalny.
	const [provider, setProvider] = useState("oci_gemini");
	const [openaiModel, setOpenaiModel] = useState("gpt-4o-mini");
	const [cap, setCap] = useState<string>("");
	const [shortExpiryDays, setShortExpiryDays] = useState("60");
	useEffect(() => {
		if (!data?.config) return;
		setProvider(data.config.provider ?? "oci_gemini");
		setOpenaiModel(data.config.openaiModel ?? "gpt-4o-mini");
		setCap(data.config.dailyTokenCap != null ? String(data.config.dailyTokenCap) : "");
	}, [data?.config]);
	useEffect(() => {
		if (businessConfig.data) setShortExpiryDays(String(businessConfig.data.shortExpiryDays));
	}, [businessConfig.data]);

	const save = useMutation({
		mutationFn: () =>
			apiFetch<LlmConfig>("/strateg-cenowy/asystent-llm-config", {
				method: "POST",
				body: JSON.stringify({
					provider,
					openaiModel,
					dailyTokenCap: cap.trim() === "" ? null : Number(cap),
				}),
			}),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["asystent", "stats"] }),
	});
	const parsedShortExpiryDays = Number(shortExpiryDays);
	const shortExpiryDaysValid =
		Number.isInteger(parsedShortExpiryDays) &&
		parsedShortExpiryDays >= 1 &&
		parsedShortExpiryDays <= 365;
	const saveBusinessConfig = useMutation({
		mutationFn: () => updateAsystentBusinessConfig({ shortExpiryDays: parsedShortExpiryDays }),
		onSuccess: (config) => {
			queryClient.setQueryData(["asystent", "business-config"], config);
			queryClient.invalidateQueries({ queryKey: ["asystent", "search"] });
			queryClient.invalidateQueries({ queryKey: ["asystent-enrich"] });
		},
	});

	if (isLoading) {
		return (
			<div className="flex h-full items-center justify-center text-muted-foreground">
				<Loader2 className="mr-2 size-4 animate-spin" /> Ładowanie statystyk…
			</div>
		);
	}
	if (error || !data) {
		return (
			<div className="flex h-full items-center justify-center text-negative">
				Błąd ładowania statystyk Asystenta.
			</div>
		);
	}

	const totalTokens = (w: WindowStat) => w.promptTokens + w.completionTokens;

	return (
		<div className="mx-auto w-full max-w-5xl space-y-6 p-4 md:p-6 max-md:space-y-4">
			{isBelowMd ? (
				// Telefon: tytul i opis w pasku gornym (PageHeader), bez powtorzenia pod nim.
				<PageHeader title="AI — koszty i statystyki" description={PAGE_DESCRIPTION} />
			) : (
				<div>
					<h1 className="text-xl font-semibold text-foreground">AI — koszty i statystyki</h1>
					<p className="text-sm text-muted-foreground">{PAGE_DESCRIPTION}</p>
				</div>
			)}

			{/* ── Postęp standaryzacji opisów AI ── */}
			{reformat.data && (reformat.data.total > 0 || (reformat.data.repairTotal ?? 0) > 0) && (
				<Card>
					<CardHeader className="pb-2">
						<div className="flex items-start justify-between gap-3">
							<div>
								<CardTitle className="text-base">Naprawa opisów AI</CardTitle>
								<CardDescription>
									Weryfikacja źródeł i przebudowa opisów z ulotek (batch co 3h).
								</CardDescription>
							</div>
							<Badge
								variant={
									reformat.data.running || reformat.data.repairRunning ? "default" : "secondary"
								}
							>
								{reformat.data.running || reformat.data.repairRunning ? "w toku" : "bezczynne"}
							</Badge>
						</div>
					</CardHeader>
					<CardContent className="space-y-5">
						{(reformat.data.repairTotal ?? 0) > 0 && (
							<ProgressRow
								label="Usuwanie opisów bez źródła i ponowne wyszukiwanie"
								done={reformat.data.repairDone ?? 0}
								total={reformat.data.repairTotal ?? 0}
								remaining={reformat.data.repairRemaining ?? 0}
							/>
						)}
						{reformat.data.total > 0 && (
							<ProgressRow
								label="Standaryzacja składni z zapisanych ulotek"
								done={reformat.data.done}
								total={reformat.data.total}
								remaining={reformat.data.remaining}
							/>
						)}
					</CardContent>
				</Card>
			)}

			{/* ── Karty kosztów per okno ── */}
			{isBelowMd ? (
				// Telefon: zwarta lista okien zamiast trzech duzych kart KPI.
				<div
					data-testid="asystent-koszty-windows-mobile"
					className="divide-y divide-border rounded-md border border-border bg-card"
				>
					{data.windows.map((w) => (
						<div key={w.label} className="flex items-start justify-between gap-3 px-3 py-2">
							<div className="min-w-0">
								<div className="text-sm text-foreground">{w.label}</div>
								<div className="text-xs tabular-nums text-muted-foreground">
									{fmtNum(w.calls)} wywołań · {fmtNum(totalTokens(w))} tokenów
								</div>
							</div>
							<div className="shrink-0 text-base font-semibold tabular-nums text-foreground">
								{fmtUsd(w.costUsd)}
							</div>
						</div>
					))}
				</div>
			) : (
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
					{data.windows.map((w) => (
						<Card key={w.label}>
							<CardHeader className="pb-2">
								<CardDescription>{w.label}</CardDescription>
								<CardTitle className="text-2xl tabular-nums">{fmtUsd(w.costUsd)}</CardTitle>
							</CardHeader>
							<CardContent className="text-xs text-muted-foreground tabular-nums">
								{fmtNum(w.calls)} wywołań · {fmtNum(totalTokens(w))} tokenów
							</CardContent>
						</Card>
					))}
				</div>
			)}

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Priorytet krótkiej daty</CardTitle>
						<CardDescription>
							Produkty z dostępną partią wygasającą w tym horyzoncie dostają badge „Kru. dat” i
							wyższy priorytet. Ustawienie obowiązuje wszystkie apteki.
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="space-y-1.5">
							<Label htmlFor="short-expiry-days">Horyzont krótkiej daty (dni)</Label>
							<Input
								id="short-expiry-days"
								type="number"
								inputMode="numeric"
								min={1}
								max={365}
								value={shortExpiryDays}
								onChange={(event) => setShortExpiryDays(event.target.value)}
								disabled={!canEditConfig}
							/>
							<p className="text-xs text-muted-foreground">Zakres 1–365 dni; domyślnie 60.</p>
						</div>
						<div className="flex items-center gap-3 max-md:flex-wrap">
							{canEditConfig ? (
								<>
									<Button
										className="max-md:h-11"
										onClick={() => saveBusinessConfig.mutate()}
										disabled={
											businessConfig.isPending ||
											saveBusinessConfig.isPending ||
											!shortExpiryDaysValid
										}
									>
										{saveBusinessConfig.isPending ? (
											<Loader2 className="mr-2 size-4 animate-spin" />
										) : (
											<Save className="mr-2 size-4" />
										)}
										Zapisz
									</Button>
									{saveBusinessConfig.isSuccess && (
										<span className="text-xs text-success">Zapisano</span>
									)}
								</>
							) : (
								<ReadOnlyConfigHint />
							)}
							{(businessConfig.isError || saveBusinessConfig.isError) && (
								<span className="text-xs text-negative">Błąd ustawienia</span>
							)}
						</div>
					</CardContent>
				</Card>

				{/* ── Przełącznik providera + bezpiecznik ── */}
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Model i bezpiecznik kosztów</CardTitle>
						<CardDescription>
							Przełącz silnik LLM i ustaw dzienny limit tokenów (po przekroczeniu strażnik+korekta
							wyłączają się — ochrona przed rachunkiem).
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="space-y-1.5">
							<Label>Provider</Label>
							<Select value={provider} onValueChange={setProvider} disabled={!canEditConfig}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="oci_gemini">OCI Gemini 2.5 Flash (zalecane)</SelectItem>
									<SelectItem value="openai">OpenAI (fallback: Gemini przy 429)</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{provider === "openai" && (
							<div className="space-y-1.5">
								<Label>Model OpenAI</Label>
								<Input
									value={openaiModel}
									onChange={(e) => setOpenaiModel(e.target.value)}
									disabled={!canEditConfig}
								/>
							</div>
						)}
						<div className="space-y-1.5">
							<Label>Dzienny limit tokenów (pusty = bez limitu)</Label>
							<Input
								type="number"
								inputMode="numeric"
								placeholder="np. 5000000"
								value={cap}
								onChange={(e) => setCap(e.target.value)}
								disabled={!canEditConfig}
							/>
						</div>
						{canEditConfig ? (
							<div className="flex items-center gap-3 max-md:flex-wrap">
								<Button
									className="max-md:h-11"
									onClick={() => save.mutate()}
									disabled={save.isPending}
								>
									{save.isPending ? (
										<Loader2 className="mr-2 size-4 animate-spin" />
									) : (
										<Save className="mr-2 size-4" />
									)}
									Zapisz
								</Button>
								{save.isSuccess && <span className="text-xs text-success">Zapisano</span>}
								{save.isError && <span className="text-xs text-negative">Błąd zapisu</span>}
								<span className="text-xs text-muted-foreground">
									Zmiana działa w ~30 s (cache).
								</span>
							</div>
						) : (
							<ReadOnlyConfigHint />
						)}
					</CardContent>
				</Card>

				{/* ── Per model + per typ ── */}
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Rozbicie (30 dni)</CardTitle>
						<CardDescription>
							{fmtNum(data.totalCalls)} wywołań · śr. latencja {Math.round(data.avgLatencyMs)} ms
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<div>
							<p className="mb-1 text-xs font-medium uppercase text-muted-foreground">Per obszar</p>
							<StatTable
								rows={data.byFeature.map((f) => ({
									label: featureLabel(f.feature),
									calls: f.calls,
									tokens: f.promptTokens + f.completionTokens,
									cost: f.costUsd,
								}))}
							/>
						</div>
						<div>
							<p className="mb-1 text-xs font-medium uppercase text-muted-foreground">Per model</p>
							<StatTable
								rows={data.byModel.map((m) => ({
									label: m.model,
									sub: m.provider,
									calls: m.calls,
									tokens: m.promptTokens + m.completionTokens,
									cost: m.costUsd,
								}))}
							/>
						</div>
						<div>
							<p className="mb-1 text-xs font-medium uppercase text-muted-foreground">Per typ</p>
							<StatTable
								rows={data.byCallType.map((c) => ({
									label: callTypeLabel(c.callType),
									calls: c.calls,
									tokens: c.promptTokens + c.completionTokens,
									cost: c.costUsd,
								}))}
							/>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* ── Top zapytania użytkowników ── */}
			<Card>
				<CardHeader>
					<CardTitle className="text-base">Najczęstsze zapytania (30 dni)</CardTitle>
					<CardDescription>Co realnie wpisują użytkownicy (surowy tekst korekty).</CardDescription>
				</CardHeader>
				<CardContent>
					{data.topQueries.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							Brak danych — log zacznie się zapełniać po wyszukiwaniach.
						</p>
					) : (
						<div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
							{data.topQueries.map((q) => (
								<div
									key={q.query}
									className="flex items-center justify-between border-b py-1 text-sm"
								>
									<span
										className="truncate text-foreground max-md:min-w-0 max-md:whitespace-normal max-md:break-words"
										title={q.query}
									>
										{q.query}
									</span>
									<span className="ml-2 shrink-0 tabular-nums text-muted-foreground">
										{q.count}×
									</span>
								</div>
							))}
						</div>
					)}
				</CardContent>
			</Card>

			<p className="flex items-start gap-1.5 text-xs text-muted-foreground">
				<AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
				{data.priceNote}
			</p>
		</div>
	);
}

function StatTable({
	rows,
}: {
	rows: { label: string; sub?: string; calls: number; tokens: number; cost: number }[];
}) {
	if (rows.length === 0) {
		return <p className="text-xs text-muted-foreground">Brak danych.</p>;
	}
	return (
		// Telefon: mniejsze odstepy i zawijana etykieta zamiast ucietej (bez hovera
		// na `title` pelna nazwa modelu bylaby nieosiagalna).
		<div className="divide-y text-sm max-md:text-xs">
			<div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 pb-1 text-[11px] uppercase text-muted-foreground max-md:gap-2">
				<span>Pozycja</span>
				<span className="text-right">Wywołań</span>
				<span className="text-right">Tokeny</span>
				<span className="text-right">Koszt</span>
			</div>
			{rows.map((r) => (
				<div
					key={r.label}
					className="grid grid-cols-[1fr_auto_auto_auto] gap-3 py-1 tabular-nums max-md:gap-2"
				>
					<span
						className="min-w-0 truncate text-foreground max-md:whitespace-normal max-md:break-words"
						title={r.label}
					>
						{r.label}
						{r.sub && <span className="ml-1 text-xs text-muted-foreground">{r.sub}</span>}
					</span>
					<span className="text-right text-muted-foreground">{fmtNum(r.calls)}</span>
					<span className="text-right text-muted-foreground">{fmtNum(r.tokens)}</span>
					<span className="text-right">{fmtUsd(r.cost)}</span>
				</div>
			))}
		</div>
	);
}
